import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { makeStyles, Button, Dialog } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import * as cottageSelectors from 'selectors/cottage';
import { clearCottage, fetchCottageRequest, updateCottageRequest } from 'actions/cottage';
import Success from 'components/shared/Success';
import CalendarForm from '../components/Forms/Calendar';

const styles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('md')]: {
      minWidth: 640,
    },
  },
  dialogPaper: {
    overflow: 'hidden',
  },
}));

const UpdateCalendarDialog = ({ cottageId, disabledButtons }) => {
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = styles();
  const dispatch = useDispatch();
  const cottage = useSelector(cottageSelectors.data);
  const success = useSelector(cottageSelectors.updateSuccess);
  const isLoading = useSelector(cottageSelectors.isFetching);
  const updatingDialogs = useSelector(cottageSelectors.updatingDialogs);
  const [opened, setOpened] = React.useState(false);

  const onOpen = () => {
    dispatch(fetchCottageRequest({ cottageId }));
    setOpened(true);
  };

  const onClose = () => {
    setOpened(false);
    dispatch(clearCottage());
  };

  const onSubmit = (values) => {
    dispatch(
      updateCottageRequest({
        cottageId,
        dialog: 'calendar',
        values: {
          ...values,
        },
      }),
    );
  };

  // Periods kept booked despite this save (an active booking covers them).
  const keptBooked = (cottage && cottage.calendar_kept_booked) || [];

  React.useEffect(() => {
    // Auto-close on a clean save. If some dates were kept booked, stay open so
    // the host actually sees the warning rather than it flashing by (STU-258).
    if (success && success.code === 'informationSaved' && keptBooked.length === 0) {
      setTimeout(() => {
        onClose();
      }, 500);
    }
  }, [success]);

  const open = !isEmpty(cottage) && opened && !isLoading;

  return (
    <>
      <Button size="small" color="primary" disabled={disabledButtons} onClick={onOpen}>
        <FormattedMessage id="Update calendar" defaultMessage="Update calendar" />
      </Button>
      <Success success={success} />
      {open && (
        <Dialog
          fullScreen={fullscreen}
          fullWidth
          maxWidth="lg"
          onClose={onClose}
          open={open}
          classes={{
            paper: classNames(classes.dialogPaper),
          }}
        >
          {keptBooked.length > 0 && (
            <Alert severity="error" elevation={3}>
              <FormattedMessage
                id="cottages.calendar.keptBooked"
                defaultMessage="Some dates were kept booked because an active booking still covers them: {dates}. To free them, cancel the booking instead."
                values={{ dates: keptBooked.map((k) => `${k.from} – ${k.to}`).join(', ') }}
              />
            </Alert>
          )}
          <CalendarForm
            isLoading={isLoading || updatingDialogs.calendar}
            onClose={onClose}
            onSubmit={onSubmit}
            cottage={cottage}
          />
        </Dialog>
      )}
    </>
  );
};

UpdateCalendarDialog.propTypes = {
  cottageId: PropTypes.number.isRequired,
  disabledButtons: PropTypes.bool,
};

export default UpdateCalendarDialog;
